import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import useAuthStore from '../store/useAuthStore'
import { authService } from '../services/api/authService'

export const useSettings = () => {
    const { user, setUser, getMe } = useAuthStore()
    const [isChangingPassword, setIsChangingPassword] = useState(false)
    const [isSavingProfile, setIsSavingProfile] = useState(false)
    const [notificationPrefs, setNotificationPrefs] = useState({
        emailCompletedScans: true,
        criticalIssueAlerts: true,
        weeklySummaryReports: false,
        teamActivityNotifications: true,
    })

    // Initialize notification preferences from user data
    useEffect(() => {
        if (user?.notificationsPreferences) {
            setNotificationPrefs({
                emailCompletedScans: user.notificationsPreferences.emailCompletedScans ?? true,
                criticalIssueAlerts: user.notificationsPreferences.criticalIssueAlerts ?? true,
                weeklySummaryReports: user.notificationsPreferences.weeklySummaryReports ?? false,
                teamActivityNotifications: user.notificationsPreferences.teamActivityNotifications ?? true,
            })
        }
    }, [user])

    // Check if user is authenticated
    const checkAuth = () => {
        const token = localStorage.getItem('accessToken')
        if (!token) {
            toast.error('Please login again')
            return false
        }
        return true
    }

    // Update profile
    const updateProfile = async (profileData) => {
        if (!checkAuth()) return

        setIsSavingProfile(true)
        try {
            const response = await authService.updateProfile(profileData)

            if (response.user) {
                setUser(response.user)
            }
            toast.success('Profile updated successfully!')
            return response
        } catch (error) {
            console.error('Profile update error:', error)
            if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.')
            } else {
                const errorMessage =
                    error.response?.data?.message || error.message || 'Failed to update profile. Please try again.'
                toast.error(errorMessage)
            }
            throw error
        } finally {
            setIsSavingProfile(false)
        }
    }

    // Update profile with notification preferences
    const updateProfileWithNotifications = async (profileData) => {
        const dataWithNotifications = {
            ...profileData,
            notificationsPreferences: {
                emailCompletedScans: notificationPrefs.emailCompletedScans,
                criticalIssueAlerts: notificationPrefs.criticalIssueAlerts,
                weeklySummaryReports: notificationPrefs.weeklySummaryReports,
                teamActivityNotifications: notificationPrefs.teamActivityNotifications,
            },
        }
        return updateProfile(dataWithNotifications)
    }

    // Save notification preferences
    const saveNotificationPreferences = async () => {
        if (!checkAuth()) return

        setIsSavingProfile(true)
        try {
            const profileData = {
                firstName: user?.firstName || user?.name?.split(' ')[0] || '',
                lastName: user?.lastName || user?.name?.split(' ')[1] || '',
                email: user?.email || '',
                countryCode: user?.countryCode || '+92',
                contact: user?.contact || '',
                notificationsPreferences: {
                    emailCompletedScans: notificationPrefs.emailCompletedScans,
                    criticalIssueAlerts: notificationPrefs.criticalIssueAlerts,
                    weeklySummaryReports: notificationPrefs.weeklySummaryReports,
                    teamActivityNotifications: notificationPrefs.teamActivityNotifications,
                },
            }

            const response = await authService.updateProfile(profileData)
            if (response.user) {
                setUser(response.user)
            }
            toast.success('Notification preferences saved!')
        } catch (error) {
            console.error('Notification preferences update error:', error)
            if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.')
            } else {
                const errorMessage =
                    error.response?.data?.message || error.message || 'Failed to save preferences. Please try again.'
                toast.error(errorMessage)
            }
        } finally {
            setIsSavingProfile(false)
        }
    }

    // Change password
    const changePassword = async ({ currentPassword, newPassword }) => {
        setIsChangingPassword(true)
        try {
            await authService.changePassword(currentPassword, newPassword)
            toast.success('Password changed successfully!')
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || error.message || 'Failed to change password. Please try again.'
            toast.error(errorMessage)
            throw error
        } finally {
            setIsChangingPassword(false)
        }
    }

    return {
        user,
        notificationPrefs,
        setNotificationPrefs,
        isChangingPassword,
        isSavingProfile,
        updateProfile,
        updateProfileWithNotifications,
        saveNotificationPreferences,
        changePassword,
    }
}
